import type { Cra } from '@bloomays-lib/types.shared';

/** Répartition des jours d'un CRA — sous-ensemble de `Cra` suffisant au calcul des montants. */
export type FreelanceWorkedDays = Pick<
  Cra,
  'normalWorkedDays' | 'saturdaysWorkedDays' | 'sundayAndholidaysWorkedDays' | 'astreinte'
>;

/** Tarif de vente minimal — compatible `Pricing` (talents) et `IPricing` (bloomers). */
export type FreelanceSalesPricingInput = {
  salePricePerDayWithoutTaxes: number;
  astreinteCoef?: number;
};

/** Tarif d'achat minimal — compatible `Pricing` (talents) et `IPricing` (bloomers). */
export type FreelancePurchasePricingInput = {
  purchasePricePerDayWithoutTaxes: number;
  astreinteCoef?: number;
};

/** Entrée tarif minimale — compatible `Pricing` (talents) et `IPricing` (bloomers). */
export type FreelanceMarginPricingInput = FreelanceSalesPricingInput & FreelancePurchasePricingInput;

/** Montant HT décomposé jours normaux / HNO (heures non ouvrées) + total. */
export type FreelanceAmountParts = {
  /** Part jours ouvrés (jours travaillés normaux ×1). */
  normal: number;
  /** Part HNO : samedi ×1.5 + dimanche/férié ×2 + astreinte ×`astreinteCoef`. */
  hno: number;
  /** Somme normal + HNO. */
  total: number;
};

/** Composante de jours d'un CRA : jours ouvrés, samedi, dimanche / férié, astreinte. */
export type FreelanceDaysComponent = 'normal' | 'saturday' | 'sundayHoliday' | 'astreinte';

/**
 * Une composante du calcul d'un montant CRA : `jours × montant jour × coefficient`.
 * Détailler le calcul composante par composante permet de confronter un montant à la
 * ligne qui le porte (coefficient HNO, astreinte oubliée, jours en trop).
 */
export type FreelanceAmountLine = {
  component: FreelanceDaysComponent;
  days: number;
  coefficient: number;
  amount: number;
};

/** Majoration samedi. */
export const SATURDAY_MARKUP_COEF = 1.5;
/** Majoration dimanche / jour férié. */
export const SUNDAY_HOLIDAY_MARKUP_COEF = 2;
/** Coefficient d'astreinte appliqué à défaut de tarif renseigné. */
export const DEFAULT_ASTREINTE_COEF = 2;

/**
 * Montant CRA détaillé composante par composante : jours ouvrés ×1, samedi ×1.5,
 * dimanche / férié ×2, astreinte ×`astreinteCoef` (défaut 2). Source unique des
 * coefficients — `computeFreelanceAmountParts` en dérive ses parts normal / HNO, et
 * les écrans qui expliquent un montant réutilisent ces lignes plutôt que de
 * réappliquer les coefficients de leur côté.
 *
 * `amountPerDay` peut être une marge par jour (vente − achat) : les lignes suivent.
 */
export const computeFreelanceAmountLines = (
  days: FreelanceWorkedDays,
  amountPerDay: number,
  astreinteCoef = DEFAULT_ASTREINTE_COEF,
): FreelanceAmountLine[] =>
  (
    [
      { component: 'normal', days: days.normalWorkedDays, coefficient: 1 },
      { component: 'saturday', days: days.saturdaysWorkedDays, coefficient: SATURDAY_MARKUP_COEF },
      {
        component: 'sundayHoliday',
        days: days.sundayAndholidaysWorkedDays,
        coefficient: SUNDAY_HOLIDAY_MARKUP_COEF,
      },
      { component: 'astreinte', days: days.astreinte, coefficient: astreinteCoef },
    ] as const
  ).map(({ component, days: componentDays, coefficient }) => ({
    component,
    days: componentDays,
    coefficient,
    amount: amountPerDay * coefficient * componentDays,
  }));

/**
 * Méthode de calcul des montants CRA, décomposée jours normaux / HNO : jours ouvrés ×1,
 * samedi ×1.5, dimanche / férié ×2, astreinte ×`astreinteCoef` (défaut 2). Cœur partagé
 * par le calcul de marge, de CA vente et de coût achat (mêmes coefficients que
 * `calculateActivityAmount` côté API).
 */
export const computeFreelanceAmountParts = (
  days: FreelanceWorkedDays,
  amountPerDay: number,
  astreinteCoef = DEFAULT_ASTREINTE_COEF,
): FreelanceAmountParts => {
  const lines = computeFreelanceAmountLines(days, amountPerDay, astreinteCoef);
  const normal = lines.filter((line) => line.component === 'normal').reduce((total, line) => total + line.amount, 0);
  const hno = lines.filter((line) => line.component !== 'normal').reduce((total, line) => total + line.amount, 0);
  return { normal, hno, total: normal + hno };
};

/**
 * Marge mensuelle freelance à partir d’un CRA domaine et d’un tarif jour (glossaire / TEC-2767).
 */
export const computeFreelanceMargin = (cra: Cra, pricing: FreelanceMarginPricingInput): number => {
  const marginPerDay = pricing.salePricePerDayWithoutTaxes - pricing.purchasePricePerDayWithoutTaxes;
  return computeFreelanceAmountParts(cra, marginPerDay, pricing.astreinteCoef ?? DEFAULT_ASTREINTE_COEF).total;
};

/**
 * CA vente HT freelance à partir des jours d’un CRA et du prix de vente jour, en
 * réutilisant la même méthode de coefficients HNO. Sert au récap de sélection CRA.
 */
export const computeFreelanceSalesAmount = (days: FreelanceWorkedDays, pricing: FreelanceSalesPricingInput): number =>
  computeFreelanceAmountParts(
    days,
    pricing.salePricePerDayWithoutTaxes,
    pricing.astreinteCoef ?? DEFAULT_ASTREINTE_COEF,
  ).total;

/**
 * Coût achat HT freelance (montant reversé au Bloomer) à partir des jours d’un CRA
 * et du prix d’achat jour, avec la même méthode de coefficients HNO.
 */
export const computeFreelancePurchaseAmount = (
  days: FreelanceWorkedDays,
  pricing: FreelancePurchasePricingInput,
): number =>
  computeFreelanceAmountParts(
    days,
    pricing.purchasePricePerDayWithoutTaxes,
    pricing.astreinteCoef ?? DEFAULT_ASTREINTE_COEF,
  ).total;
